import { AvatarComponent } from '@/components/avatar/avatar';
import { ArrowDownIcon } from '@/components/icons/arrow-down-icon';
import { BadgeIcon } from '@/components/icons/badge-icon';
import { CinemarkCashIcon } from '@/components/icons/cinemarkcash-icon';
import { CloseIcon } from '@/components/icons/close-icon';
import { EmailIcon } from '@/components/icons/email-icon';
import { GiftCardIcon } from '@/components/icons/giftcard-icon';
import { HeartIcon } from '@/components/icons/heart-icon';
import { MoonFilledIcon } from '@/components/icons/moon-filled-icon';
import { MoonIcon } from '@/components/icons/moon-icon';
import { ProfileIcon } from '@/components/icons/profile-icon';
import { SignoutIcon } from '@/components/icons/signout-icon';
import { TicketsIcon } from '@/components/icons/tickets-icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { staticRoutes } from '@/constants/static-routes';
import { SubscriptionTier } from '@/enums/subscription-tier';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { useProfile } from '@/hooks/profile/profile';
import { useStoreCredits } from '@/hooks/profile/store-credits';
import { useFeatureFlags } from '@/hooks/state/feature-flags';
import { useAuth } from '@/hooks/use-auth';
import { clsx } from 'clsx';
import { useTheme } from 'next-themes';
import Image from 'next/image';
import QRCode from 'qrcode';
import React, { useEffect, useState } from 'react';
import { MemberRewards } from './member-rewards/member-rewards';
import { QRCodeModal } from './qr-modal/qr-code-modal';
import { UserProfileDropdownItem } from './user-profile-dropdown-item/user-profile-dropdown-item';
import userProfileStyles from './user-profile.module.scss';

const dataLinkCategory = 'Account Nav';

export const UserProfile = (): React.JSX.Element => {
  const { signOut } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const [isOpen, setIsOpen] = useState(false);
  const [hasCinemarkCash, setHasCinemarkCash] = useState(false);
  const [qrCode, setQrCode] = useState<string>();
  const { data: featureFlags } = useFeatureFlags();
  const { data: storeCreditsResponse } = useStoreCredits();
  const { trackClick } = useDataAnalytics();
  const { setTheme, theme } = useTheme();

  const isMovieClubPlatinumMember = profile?.isMovieClubMember &&
    profile.subscription?.subscriptionTier === SubscriptionTier.PLATINUM;

  const profileMembershipLabel = profile?.isMovieClubMember
    ? isMovieClubPlatinumMember ? 'Movie Club Platinum' : 'Movie Club Member'
    : 'Movie Fan Member';

  const handleSignOut = (): void => {
    trackClick({ linkCategory: dataLinkCategory, linkName: 'Sign Out' });
    signOut();
  };

  useEffect(() => {
    setHasCinemarkCash(!!storeCreditsResponse?.totalCash);
  }, [storeCreditsResponse]);

  useEffect(() => {
    const getProfileQrCode = async (): Promise<void> => {
      if (profile) {
        const qrCode = await QRCode.toDataURL(`CNK${profile.memberId}?`, {
          width: 275,
        });
        setQrCode(qrCode);
      } else {
        setQrCode(undefined);
      }
    };
    getProfileQrCode();
  }, [profile]);

  const displayUserInfo = (open: boolean): React.JSX.Element => {
    if (open) {
      return (
        <CloseIcon aria-label="Close profile menu"
          className="icon icon-primary cursor-pointer no-hover-effect dont-grow"
          data-test="button-user-info-close"
          role="img"
          viewBox="0 0 20 20"
        />
      );
    }

    return (
      <div className="flex items-center gap-ms">
        <div className={userProfileStyles.rewards}>
          <MemberRewards text="Points"
            textClassName="text-xxs md:text-xs"
            value={profile?.points || 0}
            valueClassName="text-xs md:text-base"
          />
          <MemberRewards text="Credits"
            textClassName="text-xxs md:text-xs"
            value={profile?.credits || 0}
            valueClassName="text-xs md:text-base"
          />
        </div>

        <ArrowDownIcon aria-hidden="true"
          className="icon icon-primary-stroke cursor-pointer no-hover-effect dont-grow"
          data-test="button-user-info-open"
          viewBox="0 0 20 20"
        />
      </div>
    );
  };

  if (!profile || isLoading) {
    return (
      <div className={clsx('flex items-center gap-ms', userProfileStyles['profile-skeleton'])}
        role="status"
      >
        <span className="sr-only">Loading profile...</span>
        <Skeleton className="rounded-full w-6 h-6 md:w-8 md:h-8" />
        <div className="hidden space-y-2 md:block">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-2 w-24" />
        </div>
        <div className="space-y-1 md:space-y-2">
          <Skeleton className="h-4 w-6" />
          <Skeleton className="h-2 w-6" />
        </div>
        <div className="space-y-1 md:space-y-2">
          <Skeleton className="h-4 w-6" />
          <Skeleton className="h-2 w-6" />
        </div>
        <Skeleton className="h-4 w-4" />
      </div>
    );
  }

  return (
    <DropdownMenu open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger asChild>
        <button className={userProfileStyles['profile-menu']}
          data-test="button-user-profile"
          type="button"
        >
          <div className={userProfileStyles.user}>
            <AvatarComponent avatarUrl={profile?.avatar}
              selected={isOpen && featureFlags?.enableAvatars}
              type="header"
            />

            <div className={userProfileStyles['user-detail']}>
              <span className="font-bold text-base"
                data-test="profile-name"
              >
                Hi, {profile.firstName}
              </span>
              <span className="text-xs"
                data-test="profile-membership"
              >
                {profileMembershipLabel}
              </span>
            </div>
          </div>

          {displayUserInfo(isOpen)}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className={userProfileStyles['profile-dropdown-menu']}>
        <DropdownMenuItem onSelect={e => e.preventDefault()}>
          <div className={userProfileStyles['dropdown-rewards-with-qr']}>
            <div className={userProfileStyles['dropdown-rewards']}>
              <MemberRewards useAnchor
                dataLinkCategory={dataLinkCategory}
                dataLinkName="Points"
                link="/movie-rewards/history/points"
                text="Points"
                value={profile.points || 0}
              />
              <MemberRewards useAnchor
                dataLinkCategory={dataLinkCategory}
                dataLinkName="Rewards"
                link="/movie-rewards/your-reward"
                text="Rewards"
                value={profile.rewards || 0}
              />
              <MemberRewards useAnchor
                dataLinkCategory={dataLinkCategory}
                dataLinkName="Tickets"
                link="/account/purchases"
                text={profile.tickets === 1 ? 'Ticket' : 'Tickets'}
                value={profile.tickets || 0}
              />
              <MemberRewards useAnchor
                dataLinkCategory={dataLinkCategory}
                dataLinkName="Credits"
                link="/movie-rewards/history/credits"
                text="Credits"
                value={profile.credits || 0}
              />
              {(profile.subscription.subscriptionAddOnIsActive ||
              !!profile.subscription.availablePLFCreditCount) &&
                <MemberRewards useAnchor
                  dataLinkCategory={dataLinkCategory}
                  dataLinkName="Upgrades"
                  link="/movie-rewards/history/premiumupgrades"
                  text="Upgrades"
                  value={profile.subscription.availablePLFCreditCount || 0}
                />
              }
            </div>

            <div className={userProfileStyles['qr-code-details']}>
              {qrCode &&
                <>
                  <QRCodeModal profile={profile}
                    qrCode={qrCode}
                  >
                    <a className={userProfileStyles['qr-image']}
                      data-test="profile-qr-code"
                      role="button"
                    >
                      <span className="sr-only">Scan QR code</span>
                      <Image alt="account qr code"
                        height={275}
                        src={qrCode}
                        width={275}
                      />
                    </a>
                  </QRCodeModal>
                  <p className="text-sm"
                    data-test="profile-qr-code-instructions"
                  >
                    Scan at the box office and concession stand to earn points, unlock rewards and use Movie Club credits.
                  </p>
                </>
              }
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem>
          <UserProfileDropdownItem dataLinkCategory={dataLinkCategory}
            dataLinkName="Profile"
            icon={<ProfileIcon aria-labelledby="Your AccountTitle"
              className="icon icon-primary"
              role="img"
            />}
            link="/account/profile"
            text="Your Account"
            onClick={() => setIsOpen(false)}
          />
        </DropdownMenuItem>

        <DropdownMenuItem>
          <UserProfileDropdownItem useAnchor
            dataLinkCategory={dataLinkCategory}
            dataLinkName="Tickets and Purchases"
            icon={<TicketsIcon aria-labelledby="Tickets and PurchasesTitle"
              className="icon icon-primary"
              role="img"
            />}
            link="/account/purchases"
            text="Tickets and Purchases"
            onClick={() => setIsOpen(false)}
          />
        </DropdownMenuItem>

        {/* <DropdownMenuItem>
          <UserProfileDropdownItem iconName="movierewards"
            link="/account/purchases"
            dataLinkCategory={dataLinkCategory}
            dataLinkName="Rewards Center"
            text="Rewards Center"
            onClick={() => setIsOpen(false)}
          />
        </DropdownMenuItem> */}

        <DropdownMenuItem>
          <UserProfileDropdownItem newItem
            useAnchor
            dataLinkCategory={dataLinkCategory}
            dataLinkName="Badges"
            icon={<BadgeIcon aria-hidden="true"
              className="icon icon-primary"
            />}
            link={staticRoutes.loyaltyBadges}
            text="Badges"
            onClick={() => setIsOpen(false)}
          />
        </DropdownMenuItem>

        {profile.hasWatchListItems &&
          <DropdownMenuItem>
            <UserProfileDropdownItem dataLinkCategory={dataLinkCategory}
              dataLinkName="Watch List"
              icon={<HeartIcon aria-labelledby="Watch ListTitle"
                className="icon icon-primary"
                role="img"
              />}
              link="/account/watchlist"
              text="Watch List"
              onClick={() => setIsOpen(false)}
            />
          </DropdownMenuItem>
        }

        <DropdownMenuItem>
          <UserProfileDropdownItem dataLinkCategory={dataLinkCategory}
            dataLinkName="Email Subscriptions"
            icon={<EmailIcon aria-labelledby="EmailsTitle"
              className="icon icon-primary"
              role="img"
            />}
            link="/account/subscriptions"
            text="Emails"
            onClick={() => setIsOpen(false)}
          />
        </DropdownMenuItem>

        {hasCinemarkCash &&
          <DropdownMenuItem>
            <UserProfileDropdownItem dataLinkCategory={dataLinkCategory}
              dataLinkName="Cinemark Cash"
              icon={<CinemarkCashIcon aria-labelledby="Your Cinemark CashTitle"
                className="icon icon-primary"
                role="img"
              />}
              link="/account/cinemarkcash"
              text="Your Cinemark Cash"
              onClick={() => setIsOpen(false)}
            />
          </DropdownMenuItem>
        }

        <DropdownMenuItem>
          <UserProfileDropdownItem useAnchor
            dataLinkCategory={dataLinkCategory}
            dataLinkName="Your Gift Cards"
            icon={<GiftCardIcon aria-labelledby="Your Gift CardsTitle"
              className="icon icon-primary"
              role="img"
            />}
            link="/account/giftcards"
            text="Your Gift Cards"
            onClick={() => setIsOpen(false)}
          />
        </DropdownMenuItem>

        <DropdownMenuItem>
          <UserProfileDropdownItem dataLinkCategory={dataLinkCategory}
            dataLinkName="Sign Out"
            icon={<SignoutIcon aria-labelledby="Sign OutTitle"
              className="icon icon-primary"
              role="img"
            />}
            link="#"
            text="Sign Out"
            onClick={() => handleSignOut()}
          />
        </DropdownMenuItem>

        {featureFlags?.theme?.enableThemeSelection &&
          <DropdownMenuItem>
            <UserProfileDropdownItem dataLinkCategory={dataLinkCategory}
              dataLinkName="Theme"
              icon={theme === 'dark' ? <MoonFilledIcon aria-labelledby="ThemeTitle"
                className="icon icon-primary"
                role="img"
              /> : <MoonIcon aria-labelledby="ThemeTitle"
                className="icon icon-primary"
                role="img"
              />}
              link="#"
              text={theme === 'dark' ? 'Dark' : 'Light'}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            />
          </DropdownMenuItem>
        }
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
